import type { ComponentType } from 'react'
import type { RouteObject } from 'react-router'
import type { ModuleSurfaceDefinition } from './surfaces'

export { ModuleSurface, ModuleSurfaceRendererContext } from './surfaces'
export type {
  ExternalRecordReference,
  ModuleSurfaceProps,
  ModuleSurfaceDefinition,
  ModuleSurfaceRequest,
} from './surfaces'

/** Props, über die ein Modul seinen kleinen Live-Bereich auf der Startseite meldet. */
export interface HomeWidgetProps {
  /**
   * `true`, sobald tatsächlich nutzbare Live-Informationen vorliegen.
   * Bei `false` entfernt die Shell den Live-Bereich vollständig aus der Kachel.
   */
  onAvailabilityChange: (available: boolean) => void
}

export type HomeWidgetComponent = ComponentType<HomeWidgetProps>

/**
 * Das Manifest eines Fachmoduls. Jedes Modul hat genau eine `module.config.ts`
 * im Wurzelverzeichnis, die ein solches Objekt als Default exportiert.
 *
 * @example
 * ```ts
 * // modules/wartung/module.config.ts
 * import { defineModule } from '@berky/module-contract'
 *
 * export default defineModule({
 *   id: 'wartung',
 *   title: 'Wartung',
 *   description: 'Wartungsaufträge planen und dokumentieren.',
 *   icon: 'Wrench',
 *   order: 10,
 *   apiBasePath: '/api/wartung',
 *   scopes: ['api://berky-platform/Wartung.Access'],
 *   roles: ['wartung.nutzer'],
 *   routes: () => import('./src/routes'),
 * })
 * ```
 */
export interface ModuleConfig {
  /** Kleingeschrieben, kebab-case. Muss dem Ordnernamen entsprechen. Wird zum URL-Pfad. */
  id: string
  /** Anzeigename in Navigation und Kachel. */
  title: string
  /** Ein Satz, was das Modul tut. Erscheint auf der Kachel. */
  description: string
  /** Name eines lucide-react Icons, z.B. 'Wrench'. */
  icon: string
  /** Sortierung in der Navigation. Kleiner = weiter oben. */
  order: number
  /** Basis-Pfad der Modul-API. Muss `/api/<id>` sein. */
  apiBasePath: string
  /**
   * **Hier nichts eintragen.** Der Scope kommt von der Plattform.
   *
   * Es gibt genau eine App-Registrierung für die gesamte Plattform-API und
   * darin genau einen Bereich. Welcher das ist, hängt an der Umgebung — in Test
   * ein anderer als in Produktion — und steht deshalb in der
   * Laufzeitkonfiguration, nicht im Manifest. Ein Modul kann den Wert gar nicht
   * kennen: es wird einmal gebaut und läuft in beiden Umgebungen.
   *
   * Das Feld bleibt nur erhalten, damit ältere Manifeste weiter gültig sind.
   * Steht hier etwas, wird es nicht verwendet und beim Start bemängelt.
   *
   * @deprecated Weglassen. `useApiClient()` nimmt den Scope aus der Laufzeitkonfiguration.
   */
  scopes?: string[]
  /**
   * Rollen aus dem Identity-Service. Der Nutzer braucht mindestens eine davon,
   * sonst sieht er das Modul weder in der Navigation noch als Kachel.
   * Leeres Array = für alle authentifizierten Nutzer sichtbar.
   */
  roles: string[]
  /** Lazy geladene Routes. Immer als dynamischer import(). */
  routes: () => Promise<{ default: RouteObject[] }>
  /** Versioned public surfaces, e.g. `receipts-v1`. No direct module imports. */
  surfaces?: Readonly<Record<string, ModuleSurfaceDefinition>>
  /**
   * Optionaler, lazy geladener Live-Bereich für die Startseite.
   * Das Modul liefert nur den Inhalt; Größe, Abstände und Fehlerisolation
   * bleiben bei der Shell. Ohne gemeldete Inhalte bleibt die Kachel kompakt.
   */
  homeWidget?: () => Promise<{ default: HomeWidgetComponent }>
  /** Auf false setzen, um ein Modul temporär auszublenden, ohne es zu löschen. */
  enabled?: boolean
}

/**
 * Definiert ein Modul-Manifest. Reicht das Objekt unverändert durch und sorgt
 * nur dafür, dass TypeScript beim Tippen hilft und fehlende Felder anmeckert.
 *
 * @example
 * ```ts
 * export default defineModule({ id: 'wartung', ... })
 * ```
 */
export function defineModule(config: ModuleConfig): ModuleConfig {
  return config
}

export { validateModules, moduleIdFromPath, ModuleManifestError } from './validate'
export type { ModuleSource } from './validate'
